import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Intro from "./common/Intro";
import PromptInputForAssessment from "../common/ui/PromptInputForAssessment";
import { conversationalApi } from "../../services/api";
import { jobService } from "../../services/job.service";
import { toast } from "react-toastify";
import AudioInterviewStage from "../audio/AudioInterviewStage";
import AgentInterviewStage from "../audio/AgentInterviewStage";

const ConversationalPreScreening = () => {
  const params = useParams();
  const { jobId, candidateId } = params;
  const navigate = useNavigate();
  
  // State management
  const [currentStep, setCurrentStep] = useState("intro");
  const [sessionId, setSessionId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);
  const [maxQuestions] = useState(20); // Updated to 20 questions
  const [currentPhase, setCurrentPhase] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(20 * 60); // 20 minutes in seconds
  const [timerStarted, setTimerStarted] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [modality, setModality] = useState('audio'); // "audio" | "text" — fetched from job.screeningConfig
  const [firstAiLine, setFirstAiLine] = useState('');
  
  // User inputs
  const [userAnswer, setUserAnswer] = useState("");
  
  // UI states
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [showExamInstructions, setShowExamInstructions] = useState(false);
  const [isInterviewComplete, setIsInterviewComplete] = useState(false);
  const [finalAssessment, setFinalAssessment] = useState(null);

  // Timer logic
  useEffect(() => {
    let timer;
    
    if (timerStarted && !isInterviewComplete) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Auto-submit when timer reaches zero
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timerStarted, isInterviewComplete]);

  // Auto-submit function
  const handleAutoSubmit = async () => {
    if (isSubmitting || isInterviewComplete) return;
    
    try {
      setIsSubmitting(true);
      toast.info("Time's up! Submitting your interview automatically...");
      
      // Submit final response if there's any pending answer
      if (userAnswer.trim()) {
        await handleSubmitAnswer();
      }
      
      // Force interview completion
      setIsInterviewComplete(true);
      setFinalAssessment({
        overallScore: 0,
        recommendation: 'time_expired',
        message: 'Interview ended due to time limit'
      });
      
      handleInterviewCompletion({
        overallScore: 0,
        recommendation: 'time_expired',
        message: 'Interview ended due to time limit'
      });
    } catch (error) {
      console.error("Error in auto-submit:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format time function
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Proctoring: Prevent tab changes and browser navigation
  useEffect(() => {
    if (!timerStarted || isInterviewComplete) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your interview progress will be lost.';
      return e.returnValue;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        toast.warning('⚠️ Please stay on this tab during the interview. Tab switching is not allowed.');
        // Optionally auto-submit if they switch tabs
        // handleAutoSubmit();
      }
    };

    const handleKeyDown = (e) => {
      // Prevent common keyboard shortcuts for tab switching
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'Tab' || e.key === 't' || e.key === 'w') {
          e.preventDefault();
          toast.warning('⚠️ Tab switching is not allowed during the interview.');
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [timerStarted, isInterviewComplete]);

  // Fetch job and company information
  useEffect(() => {
    const fetchJobInfo = async () => {
      try {
        const response = await jobService.getJobTitle(jobId);
        if (response.title) {
          setJobTitle(response.title);
        }
        if (response.companyName) {
          setCompanyName(response.companyName);
        }
      } catch (error) {
        console.error('Error fetching job info:', error);
      }
    };
    
    if (jobId) {
      fetchJobInfo();
    }
  }, [jobId]);

  // Add debug logging for state
  useEffect(() => {
    console.log('[ConversationalPreScreening] State:', {
      currentStep,
      sessionId,
      isVerifying,
      hasAttempted,
      invalidEmail,
      timeRemaining,
      timerStarted,
      companyName
    });
  }, [currentStep, sessionId, isVerifying, hasAttempted, invalidEmail, timeRemaining, timerStarted, companyName]);

  // Start interview automatically when candidate arrives on the test step
  useEffect(() => {
    if (currentStep === 'test' && isEmailVerified && showExamInstructions && !sessionId && !isVerifying) {
      startInterview();
    }
  }, [currentStep, isEmailVerified, showExamInstructions]);

  // Start conversational interview
  const startInterview = useCallback(async () => {
    try {
      setIsVerifying(true);
      console.log('Starting interview with:', { jobId, candidateId });
      console.log('Using API URL:', conversationalApi.defaults.baseURL);

      const response = await conversationalApi.post('/public/conversational-interview/start', {
        jobId,
        candidateId
      });

      console.log('Interview start response:', response.data);

      if (response.data.hasAttempted) {
        setHasAttempted(true);
        return false;
      }

      // Successfully started interview session
      setSessionId(response.data.data.sessionId);
      setFirstAiLine(response.data.data.question || '');
      // Read modality from job (server returns it on session if present, else default audio)
      const m = response.data.data.modality || response.data.data.job?.screeningConfig?.preScreening?.modality
              || response.data.data.job?.screeningConfig?.modality;
      if (m && m !== 'inherit') setModality(m);
      // Add the first question to conversation history
      setConversationHistory([{
        type: 'question',
        text: response.data.data.question,
        timestamp: new Date()
      }]);
      setQuestionCount(response.data.data.questionCount);
      setCurrentPhase(response.data.data.currentPhase);
      
      // Start the timer when interview begins
      setTimerStarted(true);
      
      return true;
    } catch (error) {
      console.error("Error starting interview:", error);
      if (error.response?.status === 404) {
        setInvalidEmail(true);
      } else {
        toast.error("Failed to start interview. Please try again.");
      }
      return false;
    } finally {
      setIsVerifying(false);
    }
  }, [jobId, candidateId]);

  // Start interview automatically when component mounts
  useEffect(() => {
    console.log('ConversationalPreScreening Debug:', {
      currentStep,
      sessionId,
      isVerifying,
      hasAttempted,
      invalidEmail,
      jobId,
      candidateId,
      isEmailVerified,
      showExamInstructions
    });
    
    if (currentStep === "test" && !sessionId && !isVerifying && !hasAttempted && !invalidEmail && isEmailVerified && showExamInstructions) {
      console.log('Auto-starting interview...');
      startInterview();
    }
  }, [currentStep, sessionId, isVerifying, hasAttempted, invalidEmail, jobId, candidateId, isEmailVerified, showExamInstructions, startInterview]);

  // Submit answer and get next question
  const handleSubmitAnswer = async () => {
    if (!userAnswer.trim() || isSubmitting || isInterviewComplete) return;

    try {
      setIsSubmitting(true);
      
      // Add user's answer to conversation history
      setConversationHistory(prev => [...prev, {
        type: 'answer',
        text: userAnswer,
        timestamp: new Date()
      }]);

      const response = await conversationalApi.post('/public/conversational-interview/respond', {
        sessionId,
        response: userAnswer
      });

      console.log('Response submission result:', response.data);

      if (response.data.data.isComplete) {
        // Interview completed
        setIsInterviewComplete(true);
        setFinalAssessment(response.data.data.finalAssessment);
        handleInterviewCompletion(response.data.data.finalAssessment);
      } else {
        // Get next question
        // Add new question to conversation history
        if (response.data.data.question) {
          setConversationHistory(prev => [...prev, {
            type: 'question',
            text: response.data.data.question,
            timestamp: new Date()
          }]);
        }
        setQuestionCount(response.data.data.questionCount);
        setCurrentPhase(response.data.data.currentPhase);
      }

      // Clear the input
      setUserAnswer("");

    } catch (error) {
      console.error("Error submitting answer:", error);
      
      // If session is completed, handle gracefully
      if (error.response?.data?.message?.includes('completed')) {
        setIsInterviewComplete(true);
        toast.info("Interview has already been completed.");
      } else {
        toast.error("Failed to submit answer. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle interview completion
  const handleInterviewCompletion = async (assessment) => {
    try {
      const scoreText = assessment ? `Score: ${assessment.overallScore}/100` : '';

      toast.success(`Interview completed successfully! ${scoreText}`);

      setTimeout(() => {
        toast.info("Thank you for completing the interview. We'll be in touch soon.");
      }, 1000);

      setTimeout(() => {
        navigate("/test-complete", { 
          state: { 
            assessment,
            type: 'conversational_prescreening'
          }
        });
      }, 2000);
    } catch (error) {
      console.error("Error handling interview completion:", error);
    }
  };

  // Loading overlay component
  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="w-20 h-20 rounded-full border-4 border-gray-200"></div>
          <div className="absolute top-0 left-0 w-20 h-20 rounded-full border-4 border-[#F97B5C] border-t-transparent animate-spin"></div>
        </div>
        <p className="text-gray-600 text-lg">
          {isVerifying ? "Starting your interview..." : "Processing your response..."}
        </p>
      </div>
    </div>
  );

  // Show intro screen
  if (currentStep === "intro") {
    return (
      <Intro
        type="conversationalPreScreening"
        handleStartTest={() => setCurrentStep("test")}
      />
    );
  }

  // Show attempted message if already taken
  if (hasAttempted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-red-200">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
          <h2 className="text-2xl font-semibold text-red-600 mb-4">
            Interview Already Completed
          </h2>
          <p className="text-gray-600">
            You have already completed this interview. Multiple attempts are not allowed.
          </p>
        </div>
      </div>
    );
  }

  // Show invalid candidate message
  if (invalidEmail) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-red-200">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
          <h2 className="text-2xl font-semibold text-red-600 mb-4">
            Candidate Not Found
          </h2>
          <p className="text-gray-600">
            We couldn't find your candidate information for this interview. Please check the link and try again.
          </p>
        </div>
      </div>
    );
  }

  // Main interview interface
  return (
    <div className="flex flex-col min-h-screen relative">
      {(isVerifying || isSubmitting) && <LoadingOverlay />}
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {companyName ? `${companyName} Interview` : 'Company Interview'}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                <span>Phase: {currentPhase}</span>
                <span>Questions: {questionCount}/{maxQuestions}</span>
                <span className="text-green-600">● Active</span>
              </div>
            </div>
            {timerStarted && (
              <div className={`text-lg font-semibold ${timeRemaining < 300 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                ⏰ {formatTime(timeRemaining)}
              </div>
            )}
            {/* {timeRemaining !== null && (
              <div className="text-lg font-semibold text-blue-600">
                Time: {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Audio mode — ElevenLabs Conversational AI agent (Gemini-driven, low latency) */}
      {modality === 'audio' && !isInterviewComplete && (
        <div className="flex-1 max-w-4xl mx-auto w-full p-6">
          <AgentInterviewStage
            round="preScreening"
            jobId={jobId}
            resumeId={candidateId}
            candidateName={companyName || 'there'}
            onComplete={(result) => {
              // Ignore aborted signals (no conversation ID — never actually connected)
              if (result?.aborted) return;
              setIsInterviewComplete(true);
              setFinalAssessment({
                overallScore: result?.overallScore,
                recommendation: result?.recommendation
              });
              handleInterviewCompletion?.({ overallScore: result?.overallScore, recommendation: result?.recommendation || 'pending' });
            }}
          />
        </div>
      )}

      {/* Text mode (legacy) — Full-Screen Chat Interface */}
      {modality !== 'audio' && (
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
        {/* Chat Messages */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-4 max-w-4xl mx-auto">
            {conversationHistory.map((msg, index) => (
              <div key={index} className={`flex ${msg.type === 'answer' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-3xl px-6 py-4 rounded-2xl shadow-sm ${
                  msg.type === 'answer' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <div className="text-sm font-medium mb-2">
                    {msg.type === 'answer' ? 'You' : companyName || 'Company'}
                  </div>
                  <div className="text-base whitespace-pre-wrap leading-relaxed break-words">{msg.text}</div>
                  <div className="text-xs opacity-70 mt-2">
                    {msg.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isSubmitting && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 px-6 py-4 rounded-2xl shadow-sm max-w-3xl">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                    <span className="text-sm">Processing your response...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t bg-white p-6">
          <div className="max-w-4xl mx-auto">
            <PromptInputForAssessment
              value={userAnswer}
              onChange={setUserAnswer}
              onSubmit={handleSubmitAnswer}
              loading={isSubmitting || isVerifying}
              placeholder="Type your response here..."
              showSaveButton={true}
              saveButtonText={
                isInterviewComplete
                  ? "Completed"
                  : isSubmitting
                  ? "Submitting..."
                  : `Question ${questionCount}/${maxQuestions} - Submit`
              }
              disabled={isSubmitting || isVerifying || isInterviewComplete}
            />
          </div>
        </div>
      </div>
      )}

      {/* Completion message */}
      {/* Completion strip removed:
          1. It used to display the score + recommendation to the candidate
             — reports are HR-only.
          2. After /agent/finish we no longer ship `recommendation` to the
             client, so the old `.replace()` call threw TypeError on undefined.
          3. handleInterviewCompletion navigates the candidate to
             /test-complete within 2s, which already shows the right message. */}
      {isInterviewComplete && (
        <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t border-green-200">
          <div className="max-w-7xl mx-auto p-6 text-center">
            <h3 className="text-lg font-semibold text-green-800">Interview completed</h3>
            <p className="text-sm text-gray-600 mt-1">
              Thank you for your time. The hiring team will be in touch.
            </p>
          </div>
        </div>
      )}

      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-white to-red-200" />
    </div>
  );
};

export default ConversationalPreScreening; 